import { useEffect, useState } from 'react';
import axios from 'axios';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ShieldCheck, Award, Star, Crown, Gem } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * <TrustBadge>
 * Compact reputation pill: bank-verified check + tier (Bronze/Silver/Gold/
 * Platinum) + score 0-100. Use `userId` to fetch a different member's badge,
 * or omit it to fetch the current user's own trust.
 *
 * Props
 *   userId?: string         - other member's ID (optional)
 *   score?, tier?, isBankVerified?  - prefilled values (skip API fetch)
 *   size: 'sm' | 'md' | 'lg'
 *   showScore: bool         - render the numeric score
 *   showLabel: bool         - render the tier label inline
 */
const TIER_STYLES = {
  Bronze:   { bg: 'bg-amber-100',    text: 'text-amber-800',    Icon: Star  },
  Silver:   { bg: 'bg-slate-100',    text: 'text-slate-700',    Icon: Award },
  Gold:     { bg: 'bg-yellow-100',   text: 'text-yellow-800',   Icon: Crown },
  Platinum: { bg: 'bg-violet-100',   text: 'text-violet-800',   Icon: Gem   },
};

const SIZE_STYLES = {
  sm: { wrap: 'text-xs px-2 py-0.5 gap-1',  icon: 'w-3 h-3', verified: 'w-3 h-3' },
  md: { wrap: 'text-sm px-2.5 py-1 gap-1.5', icon: 'w-4 h-4', verified: 'w-4 h-4' },
  lg: { wrap: 'text-base px-3 py-1.5 gap-2', icon: 'w-5 h-5', verified: 'w-5 h-5' },
};

export default function TrustBadge({
  userId,
  score: scoreProp,
  tier: tierProp,
  isBankVerified: verifiedProp,
  size = 'sm',
  showScore = true,
  showLabel = true,
}) {
  const [data, setData] = useState(
    scoreProp != null ? { score: scoreProp, tier: tierProp, is_bank_verified: !!verifiedProp } : null
  );
  const [loading, setLoading] = useState(scoreProp == null);

  useEffect(() => {
    if (scoreProp != null) return;
    let cancelled = false;
    (async () => {
      try {
        const url = userId
          ? `${BACKEND_URL}/api/users/${userId}/trust-badge`
          : `${BACKEND_URL}/api/users/me/trust`;
        const res = await axios.get(url, { withCredentials: true });
        if (!cancelled) setData(res.data);
      } catch {
        if (!cancelled) setData({ score: 0, tier: 'Bronze', is_bank_verified: false });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId, scoreProp]);

  if (loading || !data) {
    return (
      <span
        className="inline-flex items-center text-xs text-stone-400"
        data-testid="trust-badge-loading"
      >
        …
      </span>
    );
  }

  const tierStyle = TIER_STYLES[data.tier] || TIER_STYLES.Bronze;
  const sz = SIZE_STYLES[size] || SIZE_STYLES.sm;
  const TierIcon = tierStyle.Icon;

  return (
    <TooltipProvider delayDuration={120}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={`inline-flex items-center rounded-full font-semibold ${tierStyle.bg} ${tierStyle.text} ${sz.wrap}`}
            data-testid="trust-badge"
          >
            {data.is_bank_verified && (
              <ShieldCheck
                className={`${sz.verified} text-emerald-600`}
                data-testid="trust-badge-verified-icon"
              />
            )}
            <TierIcon className={sz.icon} />
            {showLabel && <span>{data.tier}</span>}
            {showScore && (
              <span className="opacity-70">· {data.score}</span>
            )}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <div className="space-y-1 text-xs">
            <div className="font-bold">
              {data.tier} member · Trust score {data.score}/100
            </div>
            <div className="text-stone-200">
              {data.is_bank_verified
                ? '✅ Bank-verified via Plaid (Plaid Identity match).'
                : 'Not yet bank-verified — link a bank to earn this badge.'}
            </div>
            {data.completed_rotations > 0 && (
              <div className="text-stone-200">
                🏆 {data.completed_rotations} rotation{data.completed_rotations === 1 ? '' : 's'} completed
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
